import { Box, CardContent, CardMedia, CardActionArea, Card, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { color } from '@/colors';
import { getTimeDiffInWords } from '@/utils/date.utils';
import { toMillis } from '@/utils/folder.utils';
import { I18N_KEYS } from '@/language/keys';
import { useUserStore } from '@/state/user.state';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { goToRecipe } from '@/utils/recipes';
import { DashboardInlineEditInput } from './DashboardInlineEditInput';
import type { DashboardRecipe } from '@/types/recipes';
import type { EditingNameState } from './DashboardMain';

interface RecipeCardProps {
  recipe: DashboardRecipe & { owner?: string; ownerName?: string };
  recipeEditState: EditingNameState;
  handleContextMenu: (e: React.MouseEvent<HTMLDivElement>, id: string, itemType?: 'folder' | 'recipe') => void;
  handleRenameRecipe: (id: string, name: string) => void;
  handleCancelRenameRecipe: () => void;
  isDragging?: boolean;
  onDragStart?: (e: React.DragEvent, itemId: string) => void;
  onDragEnd?: () => void;
  showOwner?: boolean;
}

export const RecipeCard = ({
  recipe,
  recipeEditState,
  handleContextMenu,
  handleRenameRecipe,
  handleCancelRenameRecipe,
  isDragging = false,
  onDragStart,
  onDragEnd,
  showOwner = false,
}: RecipeCardProps) => {
  const [hoveredCardId, setHoveredCardId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState(recipe.name);

  const { t } = useTranslation();
  const currentUser = useUserStore((state) => state.user);
  const isEditing = recipeEditState.kind === 'editing' && recipeEditState.id === recipe.id;

  useEffect(() => {
    if (isEditing) {
      setEditingValue(recipe.name);
    }
  }, [isEditing, recipe.name]);

  const handleClick = () => {
    if (!isEditing) {
      goToRecipe(recipe.id);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isEditing) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editingValue.trim()) {
        handleRenameRecipe(recipe.id, editingValue);
      } else {
        handleCancelRenameRecipe();
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelRenameRecipe();
    }
  };

  return (
    <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }} sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
      <Card
        data-testid={`recipe-card-${recipe.id}`}
        onMouseEnter={() => setHoveredCardId(recipe.id)}
        onMouseLeave={() => setHoveredCardId(null)}
        onContextMenu={(e) => {
          e.stopPropagation();
          handleContextMenu(e, recipe.id, 'recipe');
        }}
        draggable={!isEditing}
        onDragStart={(e) => onDragStart?.(e, recipe.id)}
        onDragEnd={onDragEnd}
        sx={{
          maxWidth: 345,
          background: 'none',
          backgroundColor: 'transparent !important',
          backgroundImage: 'none !important',
          boxShadow: 'none !important',
          cursor: isEditing ? 'default' : isDragging ? 'grabbing' : 'grab',
        }}
      >
        <CardActionArea
          disableRipple
          sx={{ position: 'relative' }}
          onClick={handleClick}
          data-testid={`recipe-card-body-${recipe.id}`}
        >
          <Box
            sx={{
              overflow: 'hidden',
              border: '1px solid',
              borderRadius: 2,
              borderColor: hoveredCardId === recipe.id ? color.White08_T : color.Dark_Grey,
              height: 240,
              position: 'relative',
            }}
          >
            <CardMedia
              component="img"
              height="100%"
              image={recipe.poster || '/workflow-default-cover.png'}
              alt="recipe poster"
            />
            {hoveredCardId === recipe.id && (
              <Box sx={{ position: 'absolute', inset: 0, backgroundColor: color.White08_T, pointerEvents: 'none' }} />
            )}
          </Box>
        </CardActionArea>
        <CardContent
          sx={{
            py: 1,
            pl: 1,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            backgroundImage: 'initial',
            flexDirection: 'column',
          }}
        >
          <FlexCenVer>
            <FlexCol sx={{ width: '100%' }}>
              <DashboardInlineEditInput
                isEditing={isEditing}
                value={editingValue}
                displayValue={recipe.name}
                onChange={setEditingValue}
                onKeyDown={onKeyDown}
                onBlur={handleCancelRenameRecipe}
                data-testid={`recipe-title-${recipe.id}`}
              />
              <Typography variant="body-sm-rg" sx={{ color: color.White64_T }}>
                {showOwner ? (
                  <>
                    Owned by{' '}
                    {currentUser && recipe.owner && recipe.owner === currentUser.uid ? 'you' : recipe.ownerName || '-'}
                  </>
                ) : recipe.updatedAt ? (
                  t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.SUBTITLE_LAST_EDITED, {
                    date: getTimeDiffInWords(toMillis(recipe.updatedAt)),
                  })
                ) : (
                  t(I18N_KEYS.DASHBOARD.PAGES.FILES.RECIPE_CARD.SUBTITLE_CREATED, {
                    date: getTimeDiffInWords(toMillis(recipe.createdAt)),
                  })
                )}
              </Typography>
            </FlexCol>
          </FlexCenVer>
        </CardContent>
      </Card>
    </Grid2>
  );
};
